import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as AppointmentActions from './appointment.actions';
import { AppointmentEntity } from './appointment.models';

export const APPOINTMENT_FEATURE_KEY = 'appointment';

export interface State extends EntityState<AppointmentEntity> {
  isInitialized: boolean;
  selectedId?: string;
  loading: boolean;
  updating: boolean;
  position: number;
  error?: string | null; // last none error (if any)
}

export interface AppointmentPartialState {
  readonly [APPOINTMENT_FEATURE_KEY]: State;
}

export function selectAppointmentId(appointment: AppointmentEntity): string {
  return appointment._id;
}

export const appointmentAdapter: EntityAdapter<AppointmentEntity> =
  createEntityAdapter<AppointmentEntity>({
    selectId: selectAppointmentId
  });

export const initialState: State = appointmentAdapter.getInitialState({
  // set initial required properties
  isInitialized: false,
  position: null,
  loading: false,
  updating: false
});

const appointmentReducer = createReducer(
  initialState,
  on(AppointmentActions.loadAllAppointments, state =>
    appointmentAdapter.removeAll({
      ...state,
      loading: true,
      error: null
    })
  ),
  on(AppointmentActions.loadAllAppointmentsSuccess, (state, { appointments }) =>
    appointmentAdapter.addMany(appointments, {
      ...state,
      loading: false,
      selectedId: null
    })
  ),
  on(AppointmentActions.loadAllAppointmentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(AppointmentActions.loadAppointment, state =>
    appointmentAdapter.removeAll({
      ...state,
      loading: true,
      error: null
    })
  ),
  on(AppointmentActions.loadAppointmentSuccess, (state, { appointment }) =>
    appointmentAdapter.addOne(appointment, {
      ...state,
      loading: false,
      selectedId: appointment._id
    })
  ),
  on(AppointmentActions.loadAppointmentFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(AppointmentActions.updateAppointment, state => ({
    ...state,
    updating: true,
    error: null
  })),
  on(AppointmentActions.updateAppointmentSuccess, (state, { appointment }) => {
    return appointmentAdapter.updateOne(
      { id: appointment._id, changes: appointment },
      {
        ...state,
        updating: false
      }
    );
  }),
  on(AppointmentActions.updateAppointmentFailure, (state, { error }) => ({
    ...state,
    updating: false,
    error
  })),

  on(AppointmentActions.stitchInit, state => ({
    ...state,
    isInitialized: false,
    loading: true,
    error: null
  })),
  on(AppointmentActions.stitchInitSuccess, state => ({
    ...state,
    isInitialized: true,
    loading: false
  })),
  on(AppointmentActions.stitchInitFailure, (state, { error }) => ({
    ...state,
    isInitialized: false,
    loading: false,
    error
  })),

  on(AppointmentActions.getQueuePosition, state => ({
    ...state,
    error: null
  })),
  on(AppointmentActions.getQueuePositionSuccess, (state, { position }) => ({
    ...state,
    loading: false,
    position
  })),
  on(AppointmentActions.getQueuePositionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return appointmentReducer(state, action);
}
